import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaSpinner, FaRobot, FaExclamationTriangle, FaShieldAlt, FaLightbulb, FaLayerGroup } from 'react-icons/fa';

const SEVERITY_STYLES = {
  low:      { badge: 'bg-green-100 text-green-800 border border-green-200',   bar: 'bg-green-400'  },
  medium:   { badge: 'bg-yellow-100 text-yellow-800 border border-yellow-200', bar: 'bg-yellow-400' },
  high:     { badge: 'bg-orange-100 text-orange-800 border border-orange-200', bar: 'bg-orange-400' },
  critical: { badge: 'bg-red-100 text-red-800 border border-red-200',          bar: 'bg-red-500'    },
};

const CONFIDENCE_COLORS = {
  low:    'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high:   'bg-green-100 text-green-700',
};

const CATEGORY_COLORS = {
  yard:       'bg-purple-100 text-purple-700',
  carrier:    'bg-cyan-100 text-cyan-700',
  fleet:      'bg-red-100 text-red-700',
  individual: 'bg-gray-100 text-gray-600',
};

function HealthBar({ score }) {
  const color = score >= 70 ? 'bg-green-400' : score >= 40 ? 'bg-yellow-400' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-8 text-right">{score}/100</span>
    </div>
  );
}

export default function FleetDiagnosticsModal({ isOpen, onClose }) {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [result, setResult]     = useState(null);
  const [activeTab, setActiveTab] = useState('anomalies');
  const API_BASE = process.env.REACT_APP_API_SERVER_URL;

  useEffect(() => {
    if (isOpen) {
      runFleetDiagnostics();
    }
    if (!isOpen) {
      setResult(null);
      setError(null);
      setActiveTab('anomalies');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const runFleetDiagnostics = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await axios.post(`${API_BASE}/maiApi/fleet-diagnose`, {});
      if (response.data?.error) {
        setError(response.data.error);
      } else {
        setResult(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Fleet diagnostic failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const fd = result?.fleetDiagnosis;
  const tabs = [
    { id: 'anomalies',  label: `Anomalies${fd ? ` (${fd.majorAnomalies?.length || 0})` : ''}` },
    { id: 'outliers',   label: `Outlier Devices${fd ? ` (${fd.outlierDevices?.length || 0})` : ''}` },
    { id: 'causes',     label: 'Root Causes' },
    { id: 'actions',    label: 'Actions' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FaLayerGroup className="text-violet-500 text-lg" />
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Fleet AI Diagnostics</h2>
              <p className="text-xs text-gray-400">Last 6 hours · All devices</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {fd && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-medium text-gray-700">{result.deviceCount} devices</span>
                <span>·</span>
                <span>{result.yardCount} yards</span>
              </div>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" title="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <FaSpinner className="animate-spin text-3xl text-violet-400 mb-3" />
              <p className="text-sm font-medium">Analysing fleet telemetry…</p>
              <p className="text-xs mt-1">Querying all devices · This may take 15–30 seconds</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <FaExclamationTriangle className="text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700">Fleet diagnostic failed</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
                <button onClick={runFleetDiagnostics} className="text-xs text-red-600 underline mt-2 hover:text-red-800">
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {fd && !loading && (
            <>
              {/* Executive Summary */}
              <div className="bg-violet-50 border border-violet-100 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <FaRobot className="text-violet-500 text-xs" />
                      <h3 className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Executive Summary</h3>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{fd.executiveSummary}</p>
                  </div>
                  {fd.overallHealthScore != null && (
                    <div className="w-32 flex-shrink-0">
                      <p className="text-[10px] text-gray-400 mb-1 text-right">Fleet Health</p>
                      <HealthBar score={fd.overallHealthScore} />
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-gray-100">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 text-xs font-medium rounded-t transition-colors ${
                      activeTab === tab.id
                        ? 'border-b-2 border-violet-500 text-violet-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab: Anomalies */}
              {activeTab === 'anomalies' && (
                <div className="space-y-3">
                  {!fd.majorAnomalies?.length && (
                    <p className="text-sm text-gray-400 italic">No major anomalies detected.</p>
                  )}
                  {fd.majorAnomalies?.map((a, i) => {
                    const sev = a.severity?.toLowerCase();
                    const sStyle = SEVERITY_STYLES[sev] || SEVERITY_STYLES.medium;
                    const cat = a.category?.toLowerCase();
                    return (
                      <div key={i} className="border border-gray-100 rounded-lg overflow-hidden">
                        <div className={`flex items-center justify-between px-4 py-2 ${sStyle.bar} bg-opacity-20`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sStyle.badge}`}>
                              {sev?.toUpperCase()}
                            </span>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-600'}`}>
                              {a.category?.toUpperCase()}
                            </span>
                            <span className="text-xs font-semibold text-gray-800">{a.title}</span>
                          </div>
                          <span className="text-[10px] text-gray-500 whitespace-nowrap ml-2">
                            {a.affectedDeviceCount != null ? `${a.affectedDeviceCount} devices` : ''}
                          </span>
                        </div>
                        <div className="px-4 py-3 space-y-2 text-xs text-gray-600">
                          {a.evidence && (
                            <p><span className="font-medium text-gray-700">Evidence: </span>{a.evidence}</p>
                          )}
                          {a.suspectedRootCause && (
                            <p><span className="font-medium text-gray-700">Root cause: </span>{a.suspectedRootCause}</p>
                          )}
                          {a.recommendedAction && (
                            <p><span className="font-medium text-gray-700">Action: </span>{a.recommendedAction}</p>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {a.affectedYards?.map(y => (
                              <span key={y} className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[10px]">Yard {y}</span>
                            ))}
                            {a.affectedCarriers?.map(c => (
                              <span key={c} className="bg-cyan-50 text-cyan-700 px-1.5 py-0.5 rounded text-[10px]">{c}</span>
                            ))}
                            {a.confidence && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${CONFIDENCE_COLORS[a.confidence?.toLowerCase()] || ''}`}>
                                {a.confidence} confidence
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tab: Outlier Devices */}
              {activeTab === 'outliers' && (
                <div>
                  {!fd.outlierDevices?.length && (
                    <p className="text-sm text-gray-400 italic">No outlier devices identified.</p>
                  )}
                  {fd.outlierDevices?.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-gray-100">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                            <th className="px-3 py-2 text-left">Device ID</th>
                            <th className="px-3 py-2 text-left">Yard</th>
                            <th className="px-3 py-2 text-left">Carrier</th>
                            <th className="px-3 py-2 text-left">Metric</th>
                            <th className="px-3 py-2 text-right">Value</th>
                            <th className="px-3 py-2 text-right">Fleet Avg</th>
                            <th className="px-3 py-2 text-left">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fd.outlierDevices.map((d, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2 font-mono text-[10px] text-gray-700">{d.androidId}</td>
                              <td className="px-3 py-2 text-gray-600">{d.yardNumber || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{d.carrier || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{d.metric}</td>
                              <td className="px-3 py-2 text-right text-red-600 font-medium">{d.value}</td>
                              <td className="px-3 py-2 text-right text-gray-500">{d.fleetAvg}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-[200px]">{d.outlierReason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Root Causes */}
              {activeTab === 'causes' && (
                <div className="space-y-3">
                  {!fd.likelyRootCauses?.length && (
                    <p className="text-sm text-gray-400 italic">No root causes identified.</p>
                  )}
                  {fd.likelyRootCauses?.map((rc, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <FaLightbulb className="text-amber-400 text-sm mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">{rc.hypothesis}</p>
                          {rc.evidence && (
                            <p className="text-xs text-gray-600 mt-1">{rc.evidence}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {rc.affectedCount != null && (
                              <span className="text-[10px] bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">
                                {rc.affectedCount} affected
                              </span>
                            )}
                            {rc.confidence && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${CONFIDENCE_COLORS[rc.confidence?.toLowerCase()] || ''}`}>
                                {rc.confidence} confidence
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tab: Recommended Actions */}
              {activeTab === 'actions' && (
                <div className="space-y-2">
                  {!fd.recommendedActions?.length && (
                    <p className="text-sm text-gray-400 italic">No recommended actions.</p>
                  )}
                  {fd.recommendedActions?.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 bg-green-50 border border-green-100 rounded-lg p-3">
                      <div className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {a.priority || i + 1}
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">{a.action}</p>
                        {a.targetGroup && (
                          <p className="text-[10px] text-gray-500 mt-0.5">Target: {a.targetGroup}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {result?.generatedAt && (
              <p className="text-[10px] text-gray-400">{new Date(result.generatedAt).toLocaleString()}</p>
            )}
            {result && (
              <button
                onClick={runFleetDiagnostics}
                className="text-[10px] text-violet-500 hover:text-violet-700 underline"
              >
                Re-run
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
